import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import apiClient from '../../../utils/axiosClient';
import type {
  LuckyLotteryDetailRevenueItem,
  LuckyLotteryDetailSource,
  LuckyLotteryDetailSteps,
} from '../../../types/luckyLottery.types';

type StepKey = keyof LuckyLotteryDetailSteps;
type OwnerKey = string; // step0은 'revenue', 나머지는 수익 대상(avatye 등)

const SOURCES: { key: LuckyLotteryDetailSource; label: string }[] = [
  { key: 'total', label: '합계' },
  { key: 'adpopcorn', label: '애드팝콘' },
  { key: 'buzzvil', label: '버즈빌' },
  { key: 'category', label: 'RC' },
  { key: 'naverDirect', label: '네이버 직연동' },
  { key: 'coupangSeller', label: '쿠팡 셀러' },
];

// 단계별 표시 열 (step0은 수익 대상 분배 전 원매출)
const STEPS: { key: StepKey; label: string; owners: { key: OwnerKey; label: string }[] }[] = [
  { key: 'step0', label: 'Step0', owners: [{ key: 'revenue', label: '원매출' }] },
  { key: 'step1', label: 'Step1', owners: [{ key: 'avatye', label: '아바티' }] },
  {
    key: 'step2',
    label: 'Step2',
    owners: [
      { key: 'gfactory', label: '지팩토리' },
      { key: 'fortuneLabs', label: '포츈랩스' },
    ],
  },
  {
    key: 'step3',
    label: 'Step3',
    owners: [
      { key: 'avatye', label: '아바티' },
      { key: 'guru', label: '구루' },
    ],
  },
];

const OWNER_COLUMN_COUNT = STEPS.reduce((n, s) => n + s.owners.length, 0);

const HEAD_SX = {
  fontWeight: 700,
  bgcolor: '#f8fafc',
  borderBottom: '2px solid #e2e8f0',
  borderLeft: '1px solid #e2e8f0',
  fontSize: '0.82rem',
  color: '#475569',
  whiteSpace: 'nowrap',
} as const;

// 합계 그룹은 다른 소스와 구분되도록 색을 입힌다.
const TOTAL_HEAD_SX = { bgcolor: '#475569', color: '#ffffff' } as const;
const TOTAL_CELL_SX = { bgcolor: '#cbd5e1', color: '#0f172a', fontWeight: 700 } as const;
const TOTAL_SUM_CELL_SX = { bgcolor: '#94a3b8', color: '#0f172a', fontWeight: 700 } as const;

const headSx = (source: LuckyLotteryDetailSource) =>
  source === 'total' ? { ...HEAD_SX, ...TOTAL_HEAD_SX } : HEAD_SX;

// 엑셀 헤더/본문을 만들기 위한 말단(소스 × 단계 × 주체) 열 목록
const LEAF_COLUMNS = SOURCES.flatMap((source) =>
  STEPS.flatMap((step, stepIdx) =>
    step.owners.map((owner, ownerIdx) => ({
      source,
      step,
      owner,
      isFirstOfSource: stepIdx === 0 && ownerIdx === 0,
      isFirstOfStep: ownerIdx === 0,
    })),
  ),
);

const cellSx = (source: LuckyLotteryDetailSource, bold: boolean) => {
  const base = {
    fontSize: '0.82rem',
    fontWeight: bold ? 700 : 400,
    color: bold ? '#1e293b' : '#374151',
    borderLeft: '1px solid #f1f5f9',
  };
  if (source !== 'total') return base;
  return { ...base, ...(bold ? TOTAL_SUM_CELL_SX : TOTAL_CELL_SX) };
};

const leafKey = (source: LuckyLotteryDetailSource, step: StepKey, owner: OwnerKey) =>
  `${source}-${step}-${owner}`;

const formatRate = (rate: number) => `${Number(rate.toFixed(2))}%`;

const toFileDate = (date: string) => date.replace(/-/g, '').slice(2);

const getAmount = (
  row: LuckyLotteryDetailRevenueItem,
  source: LuckyLotteryDetailSource,
  step: StepKey,
  owner: OwnerKey,
): number => {
  const amounts: Record<string, number> | undefined = row[source]?.[step];
  return amounts?.[owner] ?? 0;
};

const sumAmount = (
  rows: LuckyLotteryDetailRevenueItem[],
  source: LuckyLotteryDetailSource,
  step: StepKey,
  owner: OwnerKey,
): number => rows.reduce((acc, row) => acc + getAmount(row, source, step, owner), 0);

// 소스별 원매출(step0) 대비 각 열의 비율
// 금액이 일자별·소스별로 소수점 버림된 값이라 비율이 정확히 떨어지지 않을 수 있다.
const calculateRateByLeaf = (rows: LuckyLotteryDetailRevenueItem[]): Record<string, string> => {
  const amounts = LEAF_COLUMNS.map((c) => sumAmount(rows, c.source.key, c.step.key, c.owner.key));
  const grossBySource = LEAF_COLUMNS.reduce<Record<string, number>>(
    (acc, c, i) => ({
      ...acc,
      [c.source.key]: (acc[c.source.key] ?? 0) + (c.step.key === 'step0' ? amounts[i] : 0),
    }),
    {},
  );
  return Object.fromEntries(
    LEAF_COLUMNS.map((c, i) => {
      const gross = grossBySource[c.source.key];
      const rate = gross > 0 && amounts[i] > 0 ? formatRate((amounts[i] / gross) * 100) : '-';
      return [leafKey(c.source.key, c.step.key, c.owner.key), rate];
    }),
  );
};

type RateRow = { label: string; rates: Record<string, string> };

const toShortDate = (date: string) => date.slice(5);

// 아바티 광고 공급 수수료율이 같은 날끼리 구간을 나눠 구간별 비율 행을 만든다.
// 기간 전체를 한 번에 나누면 실제로 적용된 적 없는 섞인 비율이 보이기 때문이다.
// 구간이 하나면 기존처럼 '비율' 한 줄이다. rows는 최신 날짜가 먼저 오도록 정렬돼 있다.
const buildRateRows = (rows: LuckyLotteryDetailRevenueItem[]): RateRow[] => {
  const segments: { feeRate?: number; rows: LuckyLotteryDetailRevenueItem[] }[] = [];
  rows.forEach((row) => {
    const last = segments[segments.length - 1];
    if (last && last.feeRate === row.avatyeSupplyFeeRate) last.rows.push(row);
    else segments.push({ feeRate: row.avatyeSupplyFeeRate, rows: [row] });
  });
  if (segments.length <= 1) return [{ label: '비율', rates: calculateRateByLeaf(rows) }];
  return segments.map(({ feeRate, rows: segmentRows }) => {
    const from = toShortDate(segmentRows[segmentRows.length - 1].date);
    const to = toShortDate(segmentRows[0].date);
    const fee = feeRate === undefined ? '' : `, 아바티 ${formatRate(feeRate * 100)}`;
    return { label: `비율 (${from}~${to}${fee})`, rates: calculateRateByLeaf(segmentRows) };
  });
};

type AmountCellsProps = {
  getValue: (source: LuckyLotteryDetailSource, step: StepKey, owner: OwnerKey) => number;
  bold?: boolean;
};

function AmountCells({ getValue, bold = false }: AmountCellsProps) {
  return (
    <>
      {SOURCES.flatMap(({ key: source }) =>
        STEPS.flatMap(({ key: step, owners }) =>
          owners.map(({ key: owner }) => (
            <TableCell key={`${source}-${step}-${owner}`} align="right" sx={cellSx(source, bold)}>
              {getValue(source, step, owner).toLocaleString('ko-KR')}
            </TableCell>
          )),
        ),
      )}
    </>
  );
}

export default function LuckyLotteryRevenueDetail() {
  // 정산 현황과 같은 기본 기간(이번 달 1일 ~ 오늘)
  const [startDateValue, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDateValue, setEndDate] = useState<Dayjs | null>(dayjs());
  const startDate = startDateValue ? startDateValue.format('YYYY-MM-DD') : '';
  const endDate = endDateValue ? endDateValue.format('YYYY-MM-DD') : '';
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [data, setData] = useState<LuckyLotteryDetailRevenueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', {
        params: { start_date: startDate, end_date: endDate },
      })
      .then((res) => setAppOptions(['앱 전체', ...(res.data || [])]))
      .catch(() => undefined);
  }, [startDate, endDate]);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    const app = selectedApp === '앱 전체' ? null : selectedApp;
    setLoading(true);
    apiClient
      .get<LuckyLotteryDetailRevenueItem[]>('/api/v1/stat/lucky-lottery/detail', {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
        },
      })
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData([]);
        setErrorMessage('데이터 로드에 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => {
    fetchAppOptions();
  }, [fetchAppOptions]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedRows = useMemo(() => [...data].sort((a, b) => b.date.localeCompare(a.date)), [data]);

  const rateRows = useMemo(() => buildRateRows(sortedRows), [sortedRows]);

  // 화면 테이블과 같은 구성의 엑셀 데이터: 3행 헤더(소스/단계/주체) + 비율 행(구간별) + 합계 행 + 일별 행
  const excelRows = useMemo<(string | number)[][]>(
    () => [
      ['날짜', ...LEAF_COLUMNS.map((c) => (c.isFirstOfSource ? c.source.label : ''))],
      ['', ...LEAF_COLUMNS.map((c) => (c.isFirstOfStep ? c.step.label : ''))],
      ['', ...LEAF_COLUMNS.map((c) => c.owner.label)],
      ...rateRows.map(({ label, rates }) => [
        label,
        ...LEAF_COLUMNS.map((c) => rates[leafKey(c.source.key, c.step.key, c.owner.key)]),
      ]),
      [
        '합계',
        ...LEAF_COLUMNS.map((c) => sumAmount(sortedRows, c.source.key, c.step.key, c.owner.key)),
      ],
      ...sortedRows.map((row) => [
        row.date,
        ...LEAF_COLUMNS.map((c) => getAmount(row, c.source.key, c.step.key, c.owner.key)),
      ]),
    ],
    [sortedRows, rateRows],
  );

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        <DateRangeSelector
          startDate={startDateValue}
          endDate={endDateValue}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
        />
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      <Box sx={{ ...PAGE_CARD_SX, width: '100%', maxWidth: '100%' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
            매출 인식 현황
          </Typography>
          <TableActionsMenu
            data={sortedRows.length > 0 ? excelRows : undefined}
            filename={`럭키복권_매출인식_${toFileDate(startDate)}_${toFileDate(endDate)}.xlsx`}
            sheetName="매출 인식 현황"
          />
        </Box>

        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.82rem' }}>
          RC는 원매출에 비율을 적용한 값이라 실제 매출 현황과 다소 차이가 발생할 수 있습니다.
          <br />
          금액은 일자별·소스별로 소수점을 버림 처리하므로 하단 비율이 정확히 떨어지지 않을 수
          있습니다.
        </Alert>

        {rateRows.length > 1 && (
          <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.82rem' }}>
            선택 기간 중 아바티 광고공급 수수료가 바뀐 날이 있어 비율을 구간별로 나눠 표시합니다.
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : sortedRows.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <Typography color="#94a3b8">데이터가 없습니다.</Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 0, border: '1px solid #f1f5f9' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={3}
                    sx={{ ...HEAD_SX, borderLeft: 'none', position: 'sticky', left: 0, zIndex: 2 }}
                  >
                    날짜
                  </TableCell>
                  {SOURCES.map(({ key, label }) => (
                    <TableCell
                      key={key}
                      align="center"
                      colSpan={OWNER_COLUMN_COUNT}
                      sx={headSx(key)}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {SOURCES.flatMap(({ key: source }) =>
                    STEPS.map(({ key: step, label, owners }) => (
                      <TableCell
                        key={`${source}-${step}`}
                        align="center"
                        colSpan={owners.length}
                        sx={headSx(source)}
                      >
                        {label}
                      </TableCell>
                    )),
                  )}
                </TableRow>
                <TableRow>
                  {SOURCES.flatMap(({ key: source }) =>
                    STEPS.flatMap(({ key: step, owners }) =>
                      owners.map(({ key: owner, label }) => (
                        <TableCell
                          key={`${source}-${step}-${owner}`}
                          align="center"
                          sx={headSx(source)}
                        >
                          {label}
                        </TableCell>
                      )),
                    ),
                  )}
                </TableRow>
                {rateRows.map(({ label, rates }) => (
                  <TableRow key={label}>
                    <TableCell
                      sx={{
                        ...HEAD_SX,
                        borderLeft: 'none',
                        fontSize: '0.75rem',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                      }}
                    >
                      {label}
                    </TableCell>
                    {SOURCES.flatMap(({ key: source }) =>
                      STEPS.flatMap(({ key: step, owners }) =>
                        owners.map(({ key: owner }) => (
                          <TableCell
                            key={leafKey(source, step, owner)}
                            align="center"
                            sx={{ ...headSx(source), fontWeight: 400, fontSize: '0.75rem' }}
                          >
                            {rates[leafKey(source, step, owner)]}
                          </TableCell>
                        )),
                      ),
                    )}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      color: '#1e293b',
                      bgcolor: '#f5f5f5',
                      position: 'sticky',
                      left: 0,
                    }}
                  >
                    합계
                  </TableCell>
                  <AmountCells
                    bold
                    getValue={(source, step, owner) => sumAmount(sortedRows, source, step, owner)}
                  />
                </TableRow>
                {sortedRows.map((row) => (
                  <TableRow
                    key={row.date}
                    sx={{ bgcolor: '#fff', '&:hover': { bgcolor: '#f0f4ff' } }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        color: '#1e293b',
                        whiteSpace: 'nowrap',
                        bgcolor: 'inherit',
                        position: 'sticky',
                        left: 0,
                      }}
                    >
                      {row.date}
                    </TableCell>
                    <AmountCells
                      getValue={(source, step, owner) => getAmount(row, source, step, owner)}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
